import React, { useEffect, useCallback, ReactNode } from 'react';
import { Avatar, Button, Layout, Menu, notification, Typography } from 'antd';

import WhiteLogo from 'assets/icons/WhiteLogo.svg?react';
import Person from 'assets/icons/Person.svg?react';
import { getUser, useAuthContext } from 'contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import menuItems from 'routes/menuItems';
import BaseService from 'services/api/BaseService';
import cn from 'utils/cn';

interface AppLayoutProps {
  children: ReactNode;
  scrollable?: boolean;
}

const AppLayout = ({ children, scrollable }: AppLayoutProps) => {
  const { logout } = useAuthContext();
  const user = getUser();
  const { t } = useTranslation();
  const history = useHistory();
  const pathname = history.location.pathname;

  const handleLogout = useCallback(() => {
    logout();
    history.push('/connexion');
  }, [history, logout]);

  const visibilityChange = () => {
    if (document.visibilityState === 'visible' || !document.hidden) {
      BaseService.checkToken();
    }
  };

  const handleTokenExpired = useCallback(() => {
    handleLogout();
    notification.info({
      message: t('global:session_expired'),
    });
  }, [handleLogout, t]);

  useEffect(() => {
    window.addEventListener('onTokenExpired', handleTokenExpired);
    document.addEventListener('visibilitychange', visibilityChange);
    return () => {
      window.removeEventListener('onTokenExpired', handleTokenExpired);
      document.removeEventListener('visibilitychange', visibilityChange);
    };
  }, [handleTokenExpired]);

  return (
    <Layout className="h-screen">
      {user?.impersonated && (
        <Layout.Header className="flex items-center justify-center gap-4">
          <p className="text-white text-base">
            {t('login:impersonate', {
              name: `${user?.firstname} ${user?.lastname}`,
            })}
          </p>
          <Button onClick={handleLogout} type="default" shape="round" ghost>
            {t('global:logout')}
          </Button>
        </Layout.Header>
      )}
      <Layout>
        <Layout.Sider breakpoint="lg" collapsedWidth="0" width={224}>
          <div className="p-2 h-full w-full flex flex-col justify-between bg-blue-enterprise-6 text-blue-enterprise-1">
            <div className="flex flex-col items-center justify-between">
              <div className="flex justify-center p-4">
                <WhiteLogo />
              </div>
              <Menu
                theme="dark"
                mode="inline"
                items={menuItems}
                selectedKeys={[pathname.match(/^\/[^/]+/)?.[0] ?? '/']}
                onClick={(e) => {
                  if (typeof e?.key === 'string') history.push(e.key);
                }}
              />
            </div>
            <div>
              <div
                className="flex items-center gap-2 py-2 pl-6 cursor-pointer"
                onClick={() => history.push('/profile')}
              >
                <Avatar className="min-w-8 bg-white" icon={<Person />} />
                <Typography.Text className="text-white text-base">
                  {user?.firstname} {user?.lastname}
                </Typography.Text>
              </div>
              <div className="p-4">
                <Button
                  onClick={handleLogout}
                  type="default"
                  shape="round"
                  ghost
                  className="w-full"
                >
                  {t('global:logout')}
                </Button>
              </div>
            </div>
          </div>
        </Layout.Sider>
        <Layout.Content className="lg:p-6 overflow-hidden">
          <main className="h-full">
            <div
              className={cn(
                'h-full',
                scrollable ? 'overflow-y-auto' : 'overflow-hidden',
              )}
            >
              {children}
            </div>
          </main>
        </Layout.Content>
      </Layout>
    </Layout>
  );
};

export default React.memo(AppLayout);
